import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Grid, IconButton,
  TextField, Tabs, Tab, Box, Divider, Select, MenuItem, FormControl, InputLabel, Card, CardContent, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import QuestionMarkIcon from '@mui/icons-material/HelpOutline';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import QuizIcon from '@mui/icons-material/Quiz';
import BookIcon from '@mui/icons-material/Book';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerIcon from '@mui/icons-material/Timer';

const ExamDetailsModal = ({ open, handleClose, exam }) => {
  const tabSectionRef = useRef(null);

  const [tab, setTab] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [questionType, setQuestionType] = useState('Structured');
  const [questionText, setQuestionText] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [correctOption, setCorrectOption] = useState('');

  useEffect(() => {
    if (open && exam?.id) {
      fetch(`http://196.216.9.34:5020/api/examContent/exam/${exam.id}/questions`)
        .then(res => res.json())
        .then(data => setQuestions(data))
        .catch(err => console.error('Failed to fetch questions:', err));
    }
  }, [open, exam]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    setTimeout(() => {
      if (tabSectionRef.current) {
        tabSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  if (!exam) return null;

  const handleOptionChange = (value, index) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const addQuestion = async () => {
    if (!questionText || (questionType === 'Multiple Choice' && (!correctOption || options.some(opt => !opt)))) {
      return alert('Please complete the question fields.');
    }

    const newQuestion = {
      type: questionType,
      question: questionText,
      answer: questionType === 'Structured' ? answerText : correctOption,
      options: questionType === 'Multiple Choice' ? options : [],
    };

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`http://196.216.9.34:5020/api/examContent/exam/${exam.id}/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newQuestion),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to add question');
      }

      setQuestionText('');
      setAnswerText('');
      setOptions(['', '', '', '']);
      setCorrectOption('');

      const updatedQuestions = await fetch(`http://196.216.9.34:5020/api/examContent/exam/${exam.id}/questions`).then(res => res.json());
      setQuestions(updatedQuestions);

    } catch (err) {
      console.error(err);
      alert('Error adding question: ' + err.message);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ bgcolor: '#ff6600', color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" style={{ background: "rgba(0,0,0,0.5)", padding: "8px", borderRadius: "20px" }}>
          <Box display="flex" alignItems="center">
            <QuizIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="div">Exam Details - {exam.title}</Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Card
          sx={{
            mb: 4,
            p: 3,
            borderRadius: 4,
            bgcolor: '#ffffff',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          <Typography variant="h6" sx={{ color: '#333', mb: 3 }}>
            <QuizIcon sx={{ mr: 1, color: '#ff6600', verticalAlign: 'middle' }} />
            Exam Overview
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                label: 'Subject',
                value: exam.subject,
                icon: <BookIcon />,
              },
              {
                label: 'Exam Type',
                value: exam.type,
                icon: <QuizIcon />,
              },
              {
                label: 'Scheduled At',
                value: new Date(exam.scheduledAt).toLocaleString(),
                icon: <ScheduleIcon />,
              },
              {
                label: 'Duration',
                value: exam.duration,
                icon: <TimerIcon />,
              },
              {
                label: 'Invigilation',
                value: exam.invigilationMode,
                icon: <VisibilityIcon />,
              },
              {
                label: 'Status',
                value: exam.status,
                icon: <QuestionMarkIcon />,
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Box
                    sx={{
                      bgcolor: '#ffede0',
                      p: 1.5,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {React.cloneElement(item.icon, { sx: { color: '#ff6600' } })}
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {item.label.toUpperCase()}
                    </Typography>
                    <Typography variant="h6">{item.value}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Card>

        <Divider sx={{ mb: 2 }} />
        <Box ref={tabSectionRef}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            sx={{ mb: 2 }}
          >
            <Tab label="Add Question" icon={<NoteAddIcon />} iconPosition="start" />
            <Tab label="View Questions" icon={<VisibilityIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {tab === 0 && (
          <Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Question Type</InputLabel>
              <Select value={questionType} onChange={(e) => setQuestionType(e.target.value)} label="Question Type">
                <MenuItem value="Structured">Structured</MenuItem>
                <MenuItem value="Multiple Choice">Multiple Choice</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth label="Question" value={questionText} onChange={(e) => setQuestionText(e.target.value)}
              multiline rows={3} sx={{ mb: 2 }}
            />

            {questionType === 'Structured' ? (
              <TextField
                fullWidth label="Answer" value={answerText} onChange={(e) => setAnswerText(e.target.value)}
                multiline rows={2} sx={{ mb: 2 }}
              />
            ) : (
              <Box>
                {options.map((opt, i) => (
                  <TextField
                    key={i}
                    fullWidth
                    label={`Option ${i + 1}`}
                    value={opt}
                    onChange={(e) => handleOptionChange(e.target.value, i)}
                    sx={{ mb: 1 }}
                  />
                ))}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Correct Option</InputLabel>
                  <Select value={correctOption} onChange={(e) => setCorrectOption(e.target.value)} label="Correct Option">
                    {options.map((opt, i) => (
                      <MenuItem key={i} value={opt}>{`Option ${i + 1}: ${opt}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Button
              variant="contained"
              onClick={addQuestion}
              sx={{ bgcolor: '#ff6600', '&:hover': { bgcolor: '#e65c00' } }}
              startIcon={<NoteAddIcon />}
            >
              Add Question
            </Button>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            {questions.length === 0 ? (
              <Typography>No questions added yet.</Typography>
            ) : (
              <List>
                {questions.map((q, index) => (
                  <ListItem key={index} alignItems="flex-start">
                    <ListItemIcon>
                      <FormatListNumberedIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          Q{index + 1}: {q.question}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          {q.type === 'Multiple Choice' ? (
                            <>
                              <Typography variant="body2">Options:</Typography>
                              <ul style={{ margin: '4px 0 0 20px' }}>
                                {q.options.map((opt, i) => (
                                  <li key={i}>{opt}</li>
                                ))}
                              </ul>
                              <Typography variant="body2" color="green"><strong>Answer:</strong> {q.answer}</Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="green"><strong>Answer:</strong> {q.answer}</Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} sx={{ color: '#ff6600' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExamDetailsModal;
